import {
  Box,
  FormControl,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { featureFlagsKeys } from "../../../api/queryKeys";
import ActionStatus from "../../../components/ActionStatus";
import ContainedButton from "../../../components/buttons/ContainedButton";
import { Toast } from "../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../icons/SvgToIcon";
import { OperationalHeatmapTypes } from "../../../utils/constants";
import {
  inputIcons,
  sidebarIcons,
} from "../../stack/environment/icons/iconExport";

export default function HeatmapSettings() {
  const colors = useColors();
  const { heatmapDisplayMode } = useContext(CustomerFeatureFlagsContext);
  const { updateCurrentCustomerFeatureFlag } = useAPI();
  const queryClient = useQueryClient();

  const [selectedMode, setSelectedMode] = useState(heatmapDisplayMode);
  const [fetchStatus, setFetchStatus] = useState(null);

  const updateHeatmapModeMutation = useMutation({
    mutationFn: (newMode) =>
      updateCurrentCustomerFeatureFlag({ heatmap_display_mode: newMode }),
    onSuccess: () => {
      setFetchStatus("success");
      Toast.success("Heatmap display mode updated successfully");
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.customer });
    },
    onError: (error) => {
      setFetchStatus("error");
      Toast.error("Failed to update heatmap display mode");
    },
  });

  const handleModeChange = (event) => {
    setSelectedMode(event.target.value);
  };

  const handleSave = () => {
    setFetchStatus("loading");
    updateHeatmapModeMutation.mutate(selectedMode);
  };

  const hasChanges = selectedMode !== heatmapDisplayMode;

  return (
    <Grid
      container
      direction="column"
      gap={1}
      sx={{ padding: "0px 32px 0px 16px" }}
    >
      {/* Title Section */}
      <Grid item>
        <Grid container direction="row" gap={1} justifyContent="space-between">
          <Grid item>
            <Typography variant="subtitle1" color={colors.text_primary}>
              Heatmap Formula
            </Typography>
          </Grid>
          <Grid item>
            <ActionStatus status={fetchStatus} setStatus={setFetchStatus} />
          </Grid>
        </Grid>
      </Grid>

      {/* Description */}
      <Grid item>
        <Typography variant="body2" color={colors.text_secondary}>
          Select the formula Breeze uses to calculate risk scores in your
          heatmap.
        </Typography>
      </Grid>

      {/* Configuration Options */}
      <Grid item sx={{ mt: 4 }}>
        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <RadioGroup
            value={selectedMode}
            onChange={handleModeChange}
            sx={{ gap: 2 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Radio
                value={OperationalHeatmapTypes.ATTACK_PATH_BASED}
                sx={{
                  alignSelf: "center",
                  "&.Mui-checked": {
                    color: colors.radio_primary_checked_enabled,
                  },
                }}
              />
              <Box
                sx={{
                  border: `1px solid ${colors.grey_20percent}`,
                  borderRadius: "10px",
                  padding: 3,
                  backgroundColor:
                    selectedMode === OperationalHeatmapTypes.ATTACK_PATH_BASED
                      ? colors.primary_5percent
                      : "transparent",
                  flex: 1,
                  cursor: "pointer",
                  boxShadow:
                    selectedMode === OperationalHeatmapTypes.ATTACK_PATH_BASED
                      ? `0 0 4px ${colors.icon_input_primary}`
                      : `0 0 4px ${colors.toggle_button_selected_bg}`,
                }}
                onClick={() =>
                  setSelectedMode(OperationalHeatmapTypes.ATTACK_PATH_BASED)
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        padding: "8px",
                        backgroundColor: colors.primary_8percent,
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <SvgToIcon
                        IconLogo={sidebarIcons.ScenariosIcon}
                        width={"22px"}
                        height={"22px"}
                        style={{ color: colors.icon_input_primary }}
                      />
                    </Box>
                    <Typography variant="subtitle2" color={colors.text_primary}>
                      Attack Path Formula
                    </Typography>
                  </Box>
                  <Typography variant="body2" color={colors.grey_500}>
                    Default
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color={colors.text_secondary}
                  sx={{ mb: 3 }}
                >
                  Calculates risk based on attack scenarios, entity involvement,
                  and attack path complexity. This calculation considers
                  multiple risk factors including affected entities and scenario
                  involvement.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SvgToIcon IconLogo={inputIcons.infoIcon} width={"16px"} />
                  <Typography
                    variant="caption"
                    color={colors.text_secondary}
                    sx={{ fontSize: "12px" }}
                  >
                    Best for analyzing attacker movement and scenario impact.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Radio
                value={OperationalHeatmapTypes.CONTROLS_BASED}
                sx={{
                  alignSelf: "center",
                  "&.Mui-checked": {
                    color: colors.radio_primary_checked_enabled,
                  },
                }}
              />
              <Box
                sx={{
                  border: `1px solid ${colors.grey_20percent}`,
                  borderRadius: "10px",
                  padding: 3,
                  backgroundColor:
                    selectedMode === OperationalHeatmapTypes.CONTROLS_BASED
                      ? colors.primary_5percent
                      : "transparent",
                  flex: 1,
                  cursor: "pointer",
                  boxShadow:
                    selectedMode === OperationalHeatmapTypes.CONTROLS_BASED
                      ? `0 0 4px ${colors.icon_input_primary}`
                      : `0 0 4px ${colors.toggle_button_selected_bg}`,
                }}
                onClick={() =>
                  setSelectedMode(OperationalHeatmapTypes.CONTROLS_BASED)
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      padding: "8px",
                      backgroundColor: colors.primary_8percent,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SvgToIcon
                      IconLogo={sidebarIcons.ControlsIcon}
                      width={"22px"}
                      height={"22px"}
                      style={{ color: colors.icon_input_primary }}
                    />
                  </Box>
                  <Typography variant="subtitle2" color={colors.text_primary}>
                    Controls Formula
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color={colors.text_secondary}
                  sx={{ mb: 3 }}
                >
                  Calculates risk based on the percentage of security controls
                  that are gaps, with severity weighting. Provides a direct view
                  of control coverage gaps.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SvgToIcon
                    IconLogo={inputIcons.infoIcon}
                    style={{ color: colors.text_primary }}
                    width={"16px"}
                  />
                  <Typography
                    variant="caption"
                    color={colors.text_secondary}
                    sx={{ fontSize: "12px" }}
                  >
                    Best for understanding your coverage and control
                    effectiveness.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </RadioGroup>
        </FormControl>
      </Grid>

      {/* Save Button */}
      <Grid item>
        <Grid container direction="row" justifyContent="flex-end">
          <Grid item>
            <ContainedButton
              onClick={handleSave}
              disabled={!hasChanges || updateHeatmapModeMutation.isPending}
              loading={updateHeatmapModeMutation.isPending}
              color="primary"
              size="small"
            >
              Save Changes
            </ContainedButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
